import { CSRF_HEADER, type AiChatInput } from "@ide/shared";
import { AI_BASE, ApiError } from "./api";

export interface ChatHandlers {
  onContext?: (ctx: { files: string[]; omitted: number }) => void;
  onDelta: (text: string) => void;
  onThinking?: (text: string) => void;
  onDone?: (info: {
    stopReason: string | null;
    model: string;
    usage?: { inputTokens: number; outputTokens: number };
  }) => void;
  onError?: (err: { code: string; message: string; category?: string | null }) => void;
}

/** POST que responde com Server-Sent Events: entrega cada evento ao `onEvent`. */
async function postEventStream(
  path: string,
  body: unknown,
  onEvent: (event: string, payload: Record<string, unknown>) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(`${AI_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", [CSRF_HEADER]: "1" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    let payload: { error?: { code?: string; message?: string } } = {};
    try {
      payload = await res.json();
    } catch {
      // sem corpo JSON
    }
    throw new ApiError(
      res.status,
      payload.error?.code ?? "http_error",
      payload.error?.message ?? `Erro ${res.status}`,
    );
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (data) onEvent(event, JSON.parse(data));
    }
  }
}

type ErrorPayload = { code: string; message: string; category?: string | null };

/** Chat da IA em streaming, com o contexto do projeto. */
export function streamChat(body: AiChatInput, handlers: ChatHandlers, signal: AbortSignal): Promise<void> {
  return postEventStream(
    "/chat",
    body,
    (event, p) => {
      if (event === "delta") handlers.onDelta(p.text as string);
      else if (event === "thinking") handlers.onThinking?.(p.text as string);
      else if (event === "context") handlers.onContext?.(p as { files: string[]; omitted: number });
      else if (event === "done") handlers.onDone?.(p as Parameters<NonNullable<ChatHandlers["onDone"]>>[0]);
      else if (event === "error") handlers.onError?.(p as ErrorPayload);
    },
    signal,
  );
}
